import {
  BrandReportTimespan,
  type MonitorMediaFilterType,
  type MonitorProfileCountry,
  type MonitorProfileLanguage,
  type MonitorSocialMediaSource,
  type UserLocale,
} from '@mnd-frontend/graphql-schema';
import { RED_700 } from '@mnd-frontend/ui/shared/tokens';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';

export type BrandReportTimeProps =
  | {
      timespan: BrandReportTimespan.Range;
      fromDate: Date;
      toDate: Date;
    }
  | {
      timespan:
        | BrandReportTimespan.LastHalfYear
        | BrandReportTimespan.LastMonth
        | BrandReportTimespan.LastYear
        | BrandReportTimespan.LastQuarter;
      fromDate?: null;
      toDate?: null;
    };

type Props = {
  title: string;
  brand: string;
  color: string;
  locale?: UserLocale | null;
  coverResourceId: string;
  coverImageUrl: string;
  compareToPreviousYear: boolean;
  mediaTypes: MonitorMediaFilterType[];
  searchProfileIds: string[];
  competitorSearchProfileIds: string[][];
  excludedSourceGroups: { name: string; id: string }[];
  competitorNames: string[];
  languages: MonitorProfileLanguage[];
  countries: MonitorProfileCountry[];
  sendNotification: boolean;
  sourceHandles?: { source: MonitorSocialMediaSource; handle: string }[];
} & BrandReportTimeProps;

export const createReportDefaultColor = RED_700;

const defaultValue: Props = {
  title: '',
  brand: '',
  color: createReportDefaultColor,
  locale: null,
  coverResourceId: '',
  coverImageUrl: '',
  compareToPreviousYear: true,
  mediaTypes: [],
  timespan: BrandReportTimespan.LastQuarter,
  fromDate: null,
  toDate: null,
  searchProfileIds: [],
  competitorSearchProfileIds: [],
  excludedSourceGroups: [],
  competitorNames: [],
  languages: [],
  countries: [],
  sendNotification: true,
  sourceHandles: [],
};
type CreateBrandReportContextType = {
  state: Props;
  reset: () => void;
  setTitle: (state: string) => void;
  setState: React.Dispatch<React.SetStateAction<Props>>;
  setBrand: (brand: string) => void;
  setColor: (color: string) => void;
  setLocale: (locale: UserLocale | null) => void;
  setCompareToPreviousYear: (compare: boolean) => void;
  setMediaTypes: (mediaTypes: MonitorMediaFilterType[]) => void;
  setTime: (val: BrandReportTimeProps) => void;
  setSearchProfileIds: (profiles: string[]) => void;
  setCompetitorSearchProfileIds: (profiles: string[][]) => void;
  setCoverResourceId: (resourceId: string) => void;
  setCoverImageUrl: (url: string) => void;
  setExcludedSourceGroups: (sourceGroups: { name: string; id: string }[]) => void;
  setCompetitorNames: (names: string[]) => void;
  setLanguages: (languages: MonitorProfileLanguage[]) => void;
  setCountries: (countries: MonitorProfileCountry[]) => void;
  setSendNotification: (sendNotification: boolean) => void;
  setSourceHandles?: (
    sourceHandles: { source: MonitorSocialMediaSource; handle: string }[],
  ) => void;
};

const CreateBrandReportContext = createContext<CreateBrandReportContextType | undefined>(undefined);

export const CreateBrandReportContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<Props>({ ...defaultValue });
  const reset = useCallback(() => setState({ ...defaultValue }), []);
  const setTitle = (title: string) => setState(prevState => ({ ...prevState, title }));
  const setBrand = (brand: string) => setState(prevState => ({ ...prevState, brand }));
  const setColor = (color: string) => setState(prevState => ({ ...prevState, color }));
  const setCoverResourceId = (coverResourceId: string) =>
    setState(prevState => ({ ...prevState, coverResourceId }));
  const setCoverImageUrl = (coverImageUrl: string) =>
    setState(prevState => ({ ...prevState, coverImageUrl }));
  const setLocale = (locale: UserLocale | null) =>
    setState(prevState => ({ ...prevState, locale }));
  const setCompareToPreviousYear = (compare: boolean) =>
    setState(prevState => ({ ...prevState, compareToPreviousYear: compare }));
  const setMediaTypes = (mediaTypes: MonitorMediaFilterType[]) =>
    setState(prevState => ({ ...prevState, mediaTypes }));
  const setTime = ({ timespan, fromDate, toDate }: BrandReportTimeProps) => {
    /* for TS */
    if (timespan === BrandReportTimespan.Range && fromDate && toDate) {
      setState(prevState => ({
        ...prevState,
        timespan,
        fromDate,
        toDate,
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        timespan,
        fromDate,
        toDate,
      }));
    }
  };
  const setSearchProfileIds = (profiles: string[]) =>
    setState(prevState => ({ ...prevState, searchProfileIds: profiles }));
  const setCompetitorSearchProfileIds = (profiles: string[][]) =>
    setState(prevState => ({ ...prevState, competitorSearchProfileIds: profiles }));
  const setExcludedSourceGroups = (sourceGroups: { name: string; id: string }[]) =>
    setState(prevState => ({ ...prevState, excludedSourceGroups: sourceGroups }));
  const setCompetitorNames = (names: string[]) =>
    setState(prevState => ({ ...prevState, competitorNames: names }));
  const setLanguages = (languages: MonitorProfileLanguage[]) =>
    setState(prevState => ({ ...prevState, languages }));
  const setCountries = (countries: MonitorProfileCountry[]) =>
    setState(prevState => ({ ...prevState, countries }));
  const setSendNotification = (sendNotification: boolean) =>
    setState(prevState => ({ ...prevState, sendNotification }));
  const setSourceHandles = (
    sourceHandles: { source: MonitorSocialMediaSource; handle: string }[],
  ) => setState(prevState => ({ ...prevState, sourceHandles }));

  return (
    <CreateBrandReportContext.Provider
      value={{
        state,
        reset,
        setState,
        setBrand,
        setColor,
        setLocale,
        setCompareToPreviousYear,
        setMediaTypes,
        setTitle,
        setTime,
        setSearchProfileIds,
        setCompetitorSearchProfileIds,
        setCoverResourceId,
        setCoverImageUrl,
        setExcludedSourceGroups,
        setCompetitorNames,
        setLanguages,
        setCountries,
        setSendNotification,
        setSourceHandles,
      }}
    >
      {children}
    </CreateBrandReportContext.Provider>
  );
};

export const useCreateBrandReport = () => {
  const context = useContext(CreateBrandReportContext);

  if (!context || !context.state) {
    throw new Error('useCreateBrandReport must be used within a CreateBrandReportProvider');
  }

  return context;
};
